/**
 * `/oauth/consent?consent=<id>` — where a person decides which orgs an MCP client (Claude Code, claude.ai, the Claude
 * mobile app…) may act in as them (docs/plans/2026-10-13_MCP_OPTION_B_BUILD.md, Phase 2: Logto runs the OAuth
 * protocol, Papan decides).
 *
 * Logto's bridge starts the request for the signed-in person and sends the browser here. The person picks which ORGS
 * the client may act in — one identity each, as `gh auth login` lists organizations. Nothing is ticked by default,
 * except the orgs a previous authorization of the same client already had. There is no narrower permission to pick:
 * the grant is the person in those orgs, and the page says what that means (review fixes §1, §2.3).
 *
 * The page names the client (by its host unless the host is verified) and shows where the answer goes. The server
 * re-checks every choice (`POST /api/mcp-oauth/consent/:id/decision` — a human gate, session only) and answers with the
 * way back to Logto's consent page (built by the server, carrying a single-use ticket). A request started as another
 * account is refused. (`?request=` was #1073's own OAuth server, removed in Phase 5: such a link says it has expired.)
 *
 * Layout (owner, 2026-10-17): the same grouped cards as Settings → Connected apps (`GroupedList`), so asking for a
 * connection and managing it later look alike — the app and the product side by side, a switch per organization, what
 * the app can do and what stays with the person, then Allow / Don't allow.
 */
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'
import { BrandLogo, useBrand } from '@papan/brand/react'
import { AppTile, Group, IconRow, Notice, OrgSwitchRow, SecretsNote } from '@papan/core-ui/ui'
import { AlertCircle, ArrowLeftRight, BadgeCheck, Check, CheckCircle2, Lock, Mail, ShieldAlert } from 'lucide-react'
import { AuthLayout } from '../components/AuthLayout'
import { useAuth } from '../hooks/useAuth'
import { loginPathWithNext } from '../lib/session-loss'

interface ConsentIdentity {
  tenantId: string
  name: string
  type: string
  mode: string
  /** The org turned MCP connections off. */
  mcpOff?: boolean
  /** The org doesn't let connected AI apps use its keybook secrets (#1297). */
  mcpSecretsOff?: boolean
  emails: string[]
}

interface ConsentView {
  client: { name: string; kind: string; host?: string; redirectHost: string; clientUri: string | null; verified: boolean; publisher?: string | null; logoUrl?: string | null }
  scopes: string[]
  identities: ConsentIdentity[]
  current: { orgs: Array<{ tenantId: string; orgEmail: string; secrets?: boolean }>; scopes: string[] } | null
  expiresAt: string
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'gone'; message: string }
  | { kind: 'ready'; view: ConsentView }
  | { kind: 'leaving'; approved: boolean; client: string }

export function McpConsentPage() {
  const { t } = useTranslation('auth')
  const { productName } = useBrand()
  const { user, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const requestId = params.get('consent') ?? ''
  const apiBase = '/api/mcp-oauth/consent'

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  /** tenantId → the email it acts as there; present = ticked. */
  const [picked, setPicked] = useState<Map<string, string>>(new Map())
  const [busy, setBusy] = useState(false)
  // #1297: the orgs where this connection may use keybook secrets in app runs — per org, off unless turned on.
  const [secrets, setSecrets] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  /** The server's refusals are English; say the ones we know in the page's language. */
  const goneMessage = useCallback((code: string | undefined): string | null => {
    if (code === 'mcp_request_not_found' || code === 'mcp_request_decided') return t('mcpConnect.expired')
    if (code === 'mcp_request_other_account') return t('mcpConnect.otherAccount')
    return null
  }, [t])

  // Not signed in: sign in and come straight back — the request id stays in the URL.
  useEffect(() => {
    if (isLoading || isAuthenticated) return
    navigate(loginPathWithNext(`${location.pathname}${location.search}`), { replace: true })
  }, [isLoading, isAuthenticated, location.pathname, location.search, navigate])

  useEffect(() => {
    if (!isAuthenticated) return
    if (!/^[0-9a-f-]{36}$/i.test(requestId)) {
      setPhase({ kind: 'gone', message: t('mcpConnect.missing') })
      return
    }
    let cancelled = false
    request<ConsentView>(`${apiBase}/${requestId}`)
      .then((view) => {
        if (cancelled) return
        const usable = new Set(view.identities.filter(i => i.mode === 'full' && !i.mcpOff).map(i => i.tenantId))
        const initial = new Map<string, string>()
        for (const o of view.current?.orgs ?? []) {
          const identity = view.identities.find(i => i.tenantId === o.tenantId)
          if (identity && usable.has(o.tenantId) && identity.emails.includes(o.orgEmail)) initial.set(o.tenantId, o.orgEmail)
        }
        setPicked(initial)
        // A re-approval keeps each org's earlier secrets choice on — never dropped without the person seeing it.
        setSecrets(new Set((view.current?.orgs ?? []).filter(o => o.secrets === true && initial.has(o.tenantId)).map(o => o.tenantId)))
        setPhase({ kind: 'ready', view })
      })
      .catch((e: Error & { code?: string }) => {
        if (cancelled) return
        // The server's refusal is English; say it in the page's language when we know what it means.
        setPhase({ kind: 'gone', message: goneMessage(e.code) ?? (e.message || t('mcpConnect.failed')) })
      })
    return () => { cancelled = true }
  }, [isAuthenticated, requestId, apiBase, goneMessage, t])

  const view = phase.kind === 'ready' ? phase.view : null

  const toggleOrg = useCallback((identity: ConsentIdentity, on: boolean) => {
    setPicked((prev) => {
      const next = new Map(prev)
      if (on) next.set(identity.tenantId, prev.get(identity.tenantId) ?? identity.emails[0])
      else next.delete(identity.tenantId)
      return next
    })
  }, [])

  const setOrgEmail = useCallback((tenantId: string, email: string) => {
    setPicked((prev) => new Map(prev).set(tenantId, email))
  }, [])

  const decide = useCallback(async (approve: boolean) => {
    if (!view) return
    setBusy(true)
    setError(null)
    try {
      const orgs = [...picked].map(([tenantId, orgEmail]) => ({ tenantId, orgEmail, secrets: secrets.has(tenantId) }))
      // The grant is the person in the ticked orgs — no permission to narrow (review fixes §1); keybook secrets per org.
      const body = approve ? { approve: true, orgs } : { approve: false }
      const { redirect } = await request<{ redirect: string }>(`${apiBase}/${requestId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      setPhase({ kind: 'leaving', approved: approve, client: view.client.name })
      // Logto's consent page, built by the server from its configuration. Never a URL from this page's query.
      window.location.assign(redirect)
    } catch (e) {
      const err = e as Error & { code?: string }
      const gone = goneMessage(err.code)
      if (gone) {
        setPhase({ kind: 'gone', message: gone })
        return
      }
      setError(err.message || t('mcpConnect.failed'))
      setBusy(false)
    }
  }, [view, picked, secrets, requestId, apiBase, goneMessage, t])


  if (isLoading || !isAuthenticated || phase.kind === 'loading') {
    return <AuthLayout sheet><p className="themed-text-muted text-sm text-center">{t('common:loading')}</p></AuthLayout>
  }

  if (phase.kind === 'gone') {
    return (
      <AuthLayout sheet>
        <Notice role="alert" icon={<AlertCircle className="h-6 w-6 themed-text-muted" aria-hidden />} body={phase.message} />
      </AuthLayout>
    )
  }

  if (phase.kind === 'leaving') {
    return (
      <AuthLayout sheet>
        <Notice
          role="status"
          tone={phase.approved ? 'success' : 'neutral'}
          icon={phase.approved ? <CheckCircle2 className="h-6 w-6 text-emerald-400" aria-hidden /> : <AlertCircle className="h-6 w-6 themed-text-muted" aria-hidden />}
          title={phase.approved ? t('mcpConnect.returning', { client: phase.client }) : undefined}
          body={phase.approved ? t('mcpConnect.closeTab', { client: phase.client }) : t('mcpConnect.cancelled')}
        />
      </AuthLayout>
    )
  }

  const v = phase.view
  const canApprove = picked.size > 0 && !busy
  const client = v.client.name

  return (
    <AuthLayout sheet>
      <div className="space-y-7" data-testid="mcp-consent">
        {/* The app and the product, side by side — then the question. */}
        <header className="space-y-4 text-center">
          <div className="flex items-center justify-center gap-3" aria-hidden>
            <AppTile name={client} host={v.client.host ?? v.client.redirectHost} size="lg" logoUrl={v.client.logoUrl} />
            <ArrowLeftRight className="h-5 w-5 themed-text-muted" />
            <span className="flex h-16 w-16 items-center justify-center rounded-[18px] themed-bg-secondary border themed-border shadow-sm">
              <BrandLogo decorative className="h-10 w-10 object-contain" />
            </span>
          </div>
          <div className="space-y-1.5">
            <h2 className="text-xl font-semibold tracking-tight themed-text">{t('mcpConnect.title', { client, product: productName })}</h2>
            {v.client.verified
              ? (
                <p className="inline-flex items-center gap-1 text-xs text-blue-400">
                  <BadgeCheck className="h-3.5 w-3.5" aria-hidden />{v.client.publisher ? t('mcpConnect.verifiedBy', { publisher: v.client.publisher }) : t('mcpConnect.verified')}
                </p>
              )
              : null}
          </div>
        </header>

        {/* Stated before any choice: who this client is NOT vouched for by, and where the answer goes. */}
        {!v.client.verified && (
          <div className="flex gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm themed-text" data-testid="mcp-consent-warning">
            <ShieldAlert className="h-5 w-5 shrink-0 text-amber-400" aria-hidden />
            <p>{t('mcpConnect.unverified', { product: productName })}</p>
          </div>
        )}

        <Group
          header={t('mcpConnect.orgsLabel')}
          footer={<>
            {t('mcpConnect.orgsHint')}
            {/* What the keybook switches mean, said once — not under every org (#1297). */}
            {picked.size > 0 && <SecretsNote>{t('mcpConnect.secretsBody', { client, product: productName })}</SecretsNote>}
          </>}
        >
          {v.identities.map((identity) => {
            const unavailable = identity.mode !== 'full' || !!identity.mcpOff
            return (
              <OrgSwitchRow
                key={identity.tenantId}
                identity={identity}
                email={picked.get(identity.tenantId)}
                disabled={unavailable || busy}
                dimmed={unavailable}
                onToggle={(on) => toggleOrg(identity, on)}
                onEmail={(e) => setOrgEmail(identity.tenantId, e)}
                labels={{ noDrive: t('mcpConnect.noDrive'), orgOff: t('mcpConnect.orgOff'), asEmail: t('mcpConnect.asEmail') }}
                testId={`mcp-org-${identity.tenantId}`}
                offTestId={`mcp-org-off-${identity.tenantId}`}
                secrets={{
                  on: secrets.has(identity.tenantId),
                  // An org whose policy forbids it can't be turned on here; one already on can be turned off.
                  disabled: busy || (!!identity.mcpSecretsOff && !secrets.has(identity.tenantId)),
                  onToggle: (on) => setSecrets(prev => { const next = new Set(prev); if (on) next.add(identity.tenantId); else next.delete(identity.tenantId); return next }),
                  labels: { title: t('mcpConnect.secretsTitle'), policyOff: t('mcpConnect.secretsPolicyOff') },
                  testId: `mcp-consent-secrets-${identity.tenantId}`,
                }}
              />
            )
          })}
        </Group>

        {/* What the grant is, in one place, before the button (review fixes §2.3): with a broad grant, this IS the consent. */}
        <div className="space-y-5" data-testid="mcp-consent-grant">
          <Group header={t('mcpConnect.canTitle', { client })}>
            {(['canFiles', 'canApps', 'canAcross'] as const).map(k => (
              <IconRow key={k} icon={<Check className="h-4 w-4 text-emerald-400" />}>{t(`mcpConnect.${k}`)}</IconRow>
            ))}
            {/* Logto puts the person's email in the app's sign-in token when it asks for `email` (ChatGPT does). */}
            <IconRow icon={<Mail className="h-4 w-4 text-emerald-400" />}>{t('mcpConnect.canEmail', { email: user?.email ?? '' })}</IconRow>
          </Group>
          <Group header={t('mcpConnect.keptTitle')} footer={<>{t('mcpConnect.actsAsYou')} {t('mcpConnect.redirectTo')} <span className="font-mono">{v.client.redirectHost}</span>.</>}>
            {(['keptBin', 'keptAccess', 'keptInstall', 'keptSettings'] as const).map(k => (
              <IconRow key={k} icon={<Lock className="h-4 w-4 themed-text-muted" />}>{t(`mcpConnect.${k}`, { product: productName })}</IconRow>
            ))}
          </Group>
        </div>

        <div className="space-y-2">
          {error && <p className="text-sm text-red-400 text-center" role="alert">{error}</p>}
          {!error && picked.size === 0 && <p className="themed-text-muted text-xs text-center">{t('mcpConnect.pickOrg')}</p>}
          <button
            type="button"
            onClick={() => decide(true)}
            disabled={!canApprove}
            className="w-full rounded-xl px-4 py-2.5 text-sm font-medium themed-btn-primary disabled:opacity-50"
            data-testid="mcp-consent-authorize"
          >
            {busy ? t('mcpConnect.authorizing') : t('mcpConnect.authorize')}
          </button>
          <button
            type="button"
            onClick={() => decide(false)}
            disabled={busy}
            className="w-full rounded-xl px-4 py-2.5 text-sm themed-text themed-bg-hover disabled:opacity-50"
            data-testid="mcp-consent-cancel"
          >
            {t('mcpConnect.deny')}
          </button>
        </div>

        <p className="themed-text-muted text-xs text-center">
          {/* The request is bound to this account: another one can't answer it, so no "use another account". */}
          {t('mcpConnect.signedInAs', { email: user?.email ?? '' })}
        </p>
      </div>
    </AuthLayout>
  )
}
